import React from 'react';
import {Button, Text, View, VStack} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {
  CalloutPopoverBridge,
  CalloutPopoverContent,
} from '@packages/systems/ui/shared-ui/CalloutPopover';

// Uploaded to the Webflow assets bucket via `bin/upload-s3.ts` (see
// `AnnotationOnboardingCallout`); the filename carries a content hash.
const ONBOARDING_ASSET_SRC =
  'https://d3e54v103j8qbb.cloudfront.net/img/aicc-cms-picker-onboarding.bbb882c594.png';

const ImageView = emotionStyled('img')({
  borderRadius: '$2',
  height: 'auto',
  width: '100%',
  display: 'block',
});

export interface CmsPickerOnboardingPopoverProps {
  /** Whether the one-time callout should be visible. */
  shouldShowPopover: boolean;
  /** Permanently dismisses the FTU. Idempotent and guarded on eligibility. */
  dismiss: () => void;
  /** The AddContentPopover "+" trigger; the callout anchors to it via `asChild`. */
  children: React.ReactNode;
}

/**
 * One-time onboarding popover pointing at the AICC prompt-box "+" icon. Uses
 * `CalloutPopover` (like `AnnotationOnboardingCallout`) with `mask="NONE"` so
 * returning focus to the composer neither dismisses the hint nor closes it on
 * an outside click.
 */
export const CmsPickerOnboardingPopover = ({
  shouldShowPopover,
  dismiss,
  children,
}: CmsPickerOnboardingPopoverProps) => (
  <CalloutPopoverBridge
    asChild
    open={shouldShowPopover}
    placement="top"
    mask="NONE"
    content={() => (
      <CalloutPopoverContent
        data-automation-id="cms-picker-onboarding-callout"
        title="Connect to existing data with CMS"
        width={260}
        showCloseBtn
        closePopover={dismiss}
        hasImageInsideContent
        content={
          <VStack spacing={4}>
            <ImageView
              alt="Plus icon menu showing CMS Collection and Image options"
              src={ONBOARDING_ASSET_SRC}
            />
            <Text>
              Add data to your AI Code components through CMS Collections.
            </Text>
          </VStack>
        }
        buttonLeft={{
          type: 'custom',
          render: () => (
            <Button
              variant="outline"
              colorVariant="white"
              onClick={dismiss}
              style={{justifySelf: 'start', gridColumn: 1}}
              data-automation-id="cms-picker-onboarding-callout-dismiss"
            >
              Got it
            </Button>
          ),
        }}
      />
    )}
  >
    <View display="flex">{children}</View>
  </CalloutPopoverBridge>
);

CmsPickerOnboardingPopover.displayName = 'CmsPickerOnboardingPopover';
